// biome-ignore-all lint/suspicious/noTemplateCurlyInString: snippet syntax uses ${n:placeholder} intentionally

// Rhai language data shared with the Studio's CodeMirror editor
// (pages/NewStudio/rhaiLanguage.ts): the word lists its tokenizer
// classifies by, and the completion snippets it registers.

export const rhaiWordLists = {
  keywords: [
    "let",
    "const",
    "fn",
    "if",
    "else",
    "switch",
    "for",
    "in",
    "while",
    "loop",
    "do",
    "until",
    "break",
    "continue",
    "return",
    "throw",
    "try",
    "catch",
    "import",
    "export",
    "as",
    "private",
    "static",
    "module",
    "type",
  ],

  builtinConstants: ["true", "false", "this"],

  builtinFunctions: [
    "print",
    "debug",
    "type_of",
    "parse_int",
    "parse_float",
    "to_int",
    "to_float",
    "to_string",
    "to_upper",
    "to_lower",
    "trim",
    "len",
    "pad",
    "contains",
    "starts_with",
    "ends_with",
    "index_of",
    "sub_string",
    "split",
    "replace",
    "crop",
    "push",
    "pop",
    "shift",
    "insert",
    "remove",
    "reverse",
    "truncate",
    "drain",
    "retain",
    "splice",
    "sort",
    "map",
    "filter",
    "reduce",
    "find",
    "some",
    "all",
    "keys",
    "values",
    "is_empty",
    "call",
    "Fn",
    "is_def_fn",
    "is_def_var",
  ],

  mstreamFunctions: [
    "result",
    "timestamp_ms",
    "fill_schema",
    "fill_schema_inline",
    "mask_email",
    "mask_phone",
    "mask_year_only",
    "hash_sha256",
    "sum",
    "avg",
    "median",
    "percentile",
    "min",
    "max",
    "group_by",
    "count_by",
    "unique",
    "flatten",
    "pluck",
    "pick",
    "omit",
  ],

  typeKeywords: [
    "void",
    "bool",
    "int",
    "float",
    "string",
    "char",
    "array",
    "map",
    "timestamp",
    "decimal",
  ],
};

export interface RhaiCompletionItem {
  label: string;
  /** Snippet body in ${n:placeholder} syntax. */
  insertText: string;
  detail: string;
  documentation: string;
}

export const completionItems: RhaiCompletionItem[] = [
  // mstream custom functions
  {
    label: "result",
    insertText: "result(${1:data}, ${2:attributes})",
    detail: "(data, attributes?) → TransformResult",
    documentation:
      "Return the transform result. Pass data and optionally attributes.",
  },
  {
    label: "fill_schema",
    insertText:
      'fill_schema(${1:"schema_service"}, ${2:"resource"}, ${3:"generator_service"})',
    detail: "(schema_service, resource, generator_service) → object",
    documentation:
      "Generate a synthetic document from a registered schema. Seed and per-field rules come from the named generator service. Returning an array of generated documents fans them out into separate sink writes.",
  },
  {
    label: "fill_schema_inline",
    insertText: 'fill_schema_inline(${1:schema}, ${2:"generator_service"})',
    detail: "(schema, generator_service) → object",
    documentation:
      "Generate a synthetic document from a JSON Schema written as a script map, for shapes not worth registering.",
  },
  {
    label: "mask_email",
    insertText: "mask_email(${1:data.email})",
    detail: "(email: string) → string",
    documentation:
      'Masks an email address, preserving the first character and domain. "john@example.com" → "j***@example.com"',
  },
  {
    label: "mask_phone",
    insertText: "mask_phone(${1:data.phone})",
    detail: "(phone: string) → string",
    documentation:
      'Masks a phone number, showing only last 4 digits. "555-123-4567" → "****4567"',
  },
  {
    label: "mask_year_only",
    insertText: "mask_year_only(${1:data.date})",
    detail: "(date: string) → string",
    documentation:
      'Extracts only the year from a date string. "1990-05-15" → "1990"',
  },
  {
    label: "hash_sha256",
    insertText: "hash_sha256(${1:value})",
    detail: "(value: Dynamic) → string",
    documentation:
      "Returns the SHA-256 hash of the input value as a hex string.",
  },
  {
    label: "timestamp_ms",
    insertText: "timestamp_ms()",
    detail: "() → int",
    documentation:
      "Returns the current time in milliseconds since the Unix epoch.",
  },
  {
    label: "sum",
    insertText: "sum(${1:array})",
    detail: "(array) → number | (array, key_fn) → number",
    documentation:
      "Sum numeric values in an array. Optionally pass a key function to extract values: sum(items, |x| x.price)",
  },
  {
    label: "avg",
    insertText: "avg(${1:array})",
    detail: "(array) → number | (array, key_fn) → number",
    documentation:
      "Calculate average of numeric values. Returns () for empty arrays.",
  },
  {
    label: "median",
    insertText: "median(${1:array})",
    detail: "(array) → float | (array, key_fn) → float",
    documentation:
      "Median of the numeric values in an array, interpolated between the two middle values. Optionally pass a key function: median(items, |x| x.price). Returns () when nothing counts.",
  },
  {
    label: "percentile",
    insertText: "percentile(${1:array}, ${2:95})",
    detail: "(array, p) → float | (array, p, key_fn) → float",
    documentation:
      "Value at percentile p (0–100) of the numeric values in an array, interpolated between the two closest ranks. Optionally pass a key function: percentile(items, 95, |x| x.latency). Returns () when nothing counts.",
  },
  {
    label: "min",
    insertText: "min(${1:array})",
    detail: "(array) → Dynamic | (array, key_fn) → Dynamic",
    documentation: "Find the minimum value in an array.",
  },
  {
    label: "max",
    insertText: "max(${1:array})",
    detail: "(array) → Dynamic | (array, key_fn) → Dynamic",
    documentation: "Find the maximum value in an array.",
  },
  {
    label: "group_by",
    insertText: "group_by(${1:array}, |${2:x}| ${3:x.key})",
    detail: "(array, key_fn) → map",
    documentation:
      'Group array elements by key. Returns a map: #{ "A": [...], "B": [...] }',
  },
  {
    label: "count_by",
    insertText: "count_by(${1:array}, |${2:x}| ${3:x.key})",
    detail: "(array, key_fn) → map",
    documentation:
      'Count occurrences by key. Returns a map: #{ "active": 5, "inactive": 2 }',
  },
  {
    label: "unique",
    insertText: "unique(${1:array})",
    detail: "(array) → array | (array, key_fn) → array",
    documentation:
      "Remove duplicate values. Optionally pass a key function: unique(items, |x| x.id)",
  },
  {
    label: "flatten",
    insertText: "flatten(${1:array})",
    detail: "(array) → array",
    documentation:
      "Flatten one level of nested arrays. [[1, 2], [3, 4]] → [1, 2, 3, 4]",
  },
  {
    label: "pluck",
    insertText: 'pluck(${1:array}, "${2:field}")',
    detail: "(array, field: string) → array",
    documentation: "Extract a single field from each object in an array.",
  },
  {
    label: "pick",
    insertText: 'pick(${1:obj}, [${2:"field1", "field2"}])',
    detail: "(object, fields: array) → object",
    documentation: "Keep only the specified fields from an object.",
  },
  {
    label: "omit",
    insertText: 'omit(${1:obj}, [${2:"field1", "field2"}])',
    detail: "(object, fields: array) → object",
    documentation: "Remove the specified fields from an object.",
  },
  // Transform template
  {
    label: "transform",
    insertText: [
      "fn transform(data, attributes) {",
      "\t$0",
      "\tresult(data, attributes)",
      "}",
    ].join("\n"),
    detail: "Transform function template",
    documentation:
      "Scaffold the required transform(data, attributes) entry point.",
  },
];
